import { useState, useEffect, useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { getStudentDetail } from './api/studentsApi'
import { getStudentReceipts, getStudentLedgerWithInstallments, openStudentReceipt, downloadStudentReceipt } from './api/studentScreensApi'
import ReceiptEntryForm from './components/ReceiptEntryForm'

function KpiCard({ icon, iconBg, iconColor, label, value, valueColor = 'text-on-surface' }) {
  return (
    <div className="flex items-center gap-3.5 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-4 shadow-sm transition hover:shadow-md">
      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${iconBg} ${iconColor}`}>
        <span className="material-symbols-outlined text-xl">{icon}</span>
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant truncate">{label}</p>
        <p className={`mt-0.5 text-lg font-extrabold tracking-tight tabular-nums truncate ${valueColor}`}>
          ₹{value}
        </p>
      </div>
    </div>
  )
}

export default function StudentReceiptScreen() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const studentId = searchParams.get('id')

  const [student, setStudent] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [receipts, setReceipts] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [summary, setSummary] = useState({ paid: 0, balance: 0, overdue: 0 })

  useEffect(() => {
    loadStudent()
    loadReceipts()
    loadSummary()
  }, [studentId])

  const loadStudent = async () => {
    setIsLoading(true)
    try {
      const data = await getStudentDetail(studentId)
      setStudent(data)
    } catch {
      setStudent({ id: studentId, firstName: 'Student', lastName: '' })
    } finally {
      setIsLoading(false)
    }
  }

  const loadReceipts = async () => {
    try {
      const rows = await getStudentReceipts(studentId)
      setReceipts((rows || []).map((r) => ({ ...r, dated: r.chequeDate || r.dated || '' })))
    } catch {
      setReceipts([])
    }
  }

  const loadSummary = async () => {
    try {
      const data = await getStudentLedgerWithInstallments(studentId)
      setSummary({
        paid: data?.totalCredit || 0,
        balance: data?.balance || 0,
        overdue: data?.overdue || 0,
      })
    } catch {
      setSummary({ paid: 0, balance: 0, overdue: 0 })
    }
  }

  const goBack = () => navigate('/admission/students/list')
  const studentName = student ? `${student.firstName || ''} ${student.lastName || ''}`.trim() : 'Student'
  const rollNo = student?.rollNo || '—'
  const idCardNo = student?.idCardNo || '—'
  const totalFee = student?.totalAmount || '0.00'
  const paid = summary.paid.toLocaleString('en-IN', { minimumFractionDigits: 2 })
  const balance = summary.balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })

  return (
    <>
    {showForm ? (
      <ReceiptEntryForm
        student={student}
        studentId={studentId}
        onBack={() => setShowForm(false)}
        onSaved={() => { loadReceipts(); loadSummary() }}
      />
    ) : (
    <div className="mx-auto flex w-full max-w-[1480px] flex-col gap-6 pb-8">
      <section className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-on-surface-variant">
            <span>Admission</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span>Students</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span className="font-semibold text-primary">Receipts</span>
          </div>
          <h1 className="mt-2 text-3xl font-bold text-on-surface">
            Receipts — {studentName}
          </h1>
        </div>
        <button
          onClick={goBack}
          className="inline-flex items-center gap-2 rounded-xl bg-secondary-container px-6 py-3 text-sm font-bold text-secondary hover:bg-secondary-container/80"
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          Back to Students
        </button>
      </section>

      <div className="flex items-center justify-end">
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="inline-flex items-center rounded-xl bg-primary text-on-primary shadow-sm transition-colors hover:bg-primary/90 overflow-hidden"
          >
            <span className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold">
              <span className="material-symbols-outlined text-[18px]">add</span>
              Add New Receipt
            </span>
            <span className="h-8 w-px bg-on-primary/30" />
            <span className="flex items-center justify-center px-3 py-2.5">
              <span className="material-symbols-outlined text-[18px]">expand_more</span>
            </span>
          </button>
        </div>
      </div>

      {/* ── KPI Summary Cards (Matching ss2 format) ── */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <KpiCard
          icon="account_balance_wallet"
          iconBg="bg-blue-50"
          iconColor="text-blue-600"
          label="Total Fees"
          value={totalFee}
          valueColor="text-on-surface"
        />
        <KpiCard
          icon="check_circle"
          iconBg="bg-emerald-50"
          iconColor="text-emerald-600"
          label="Paid (Receipts)"
          value={paid}
          valueColor="text-emerald-700"
        />
        <KpiCard
          icon="receipt"
          iconBg="bg-slate-100"
          iconColor="text-slate-600"
          label="Cheques"
          value="0.00"
          valueColor="text-slate-700"
        />
        <KpiCard
          icon="event_upcoming"
          iconBg="bg-indigo-50"
          iconColor="text-indigo-600"
          label="PDCs"
          value="0.00"
          valueColor="text-indigo-700"
        />
        <KpiCard
          icon="pending"
          iconBg="bg-purple-50"
          iconColor="text-purple-600"
          label="Balance"
          value={balance}
          valueColor="text-purple-700"
        />
        <KpiCard
          icon="warning"
          iconBg="bg-amber-50"
          iconColor="text-amber-600"
          label="Due / Overdue"
          value={summary.overdue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          valueColor="text-amber-700"
        />
      </div>

      {/* ── Receipts Table Card ── */}
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
        {/* Table Title Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-outline-variant/15 bg-surface-container-low/40 px-6 py-3.5">
          <div className="flex items-center gap-2.5">
            <span className="material-symbols-outlined text-base text-primary">receipt_long</span>
            <span className="text-xs font-bold uppercase tracking-wider text-on-surface">
              Receipts for {studentName} {rollNo && rollNo !== '—' ? `(Roll No. ${rollNo} · ID Card No. ${idCardNo})` : ''}
            </span>
          </div>
          <span className="text-xs font-semibold text-on-surface-variant">
            {receipts.length} {receipts.length === 1 ? 'Receipt' : 'Receipts'}
          </span>
        </div>
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-outline-variant/30 bg-surface-container-low">
              <th className="px-5 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant">Action</th>
              <th className="px-5 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant">No.</th>
              <th className="px-5 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant">Date</th>
              <th className="px-5 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant">Type</th>
              <th className="px-5 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant">Cheque No.</th>
              <th className="px-5 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant">Dated</th>
              <th className="px-5 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant">Amount</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-5 py-16 text-center">
                  <span className="material-symbols-outlined animate-spin text-3xl text-primary/40">progress_activity</span>
                  <p className="mt-3 text-sm text-on-surface-variant">Loading receipts...</p>
                </td>
              </tr>
            ) : receipts.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-5 py-12">
                  <div className="flex items-center justify-center rounded-xl border-2 border-dashed border-blue-200 bg-blue-50/50 px-6 py-8">
                    <p className="text-center text-sm font-bold leading-relaxed text-blue-600">
                      No entries meet selected criteria — Either there are no entries in the selected
                      module or you have selected invalid filter conditions.
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              receipts.map((receipt, idx) => (
                <tr
                  key={receipt.id}
                  className={`border-b border-outline-variant/10 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-primary/5 transition-colors`}
                >
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => openStudentReceipt(receipt.id)}
                        className="inline-flex items-center gap-1 rounded-lg bg-primary/8 px-2.5 py-1 text-xs font-bold text-primary hover:bg-primary/15 transition-colors"
                      >
                        <span className="material-symbols-outlined text-[14px]">visibility</span>
                        View
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadStudentReceipt(receipt.id)}
                        className="inline-flex items-center gap-1 rounded-lg bg-secondary-container px-2.5 py-1 text-xs font-bold text-secondary hover:bg-secondary-container/80 transition-colors"
                      >
                        <span className="material-symbols-outlined text-[14px]">download</span>
                        Download
                      </button>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-sm font-semibold text-on-surface">{receipt.no}</td>
                  <td className="px-5 py-3.5 text-sm text-on-surface">{receipt.date}</td>
                  <td className="px-5 py-3.5">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold ${
                      receipt.type === 'Cash' ? 'bg-green-50 text-green-700'
                        : receipt.type === 'Cheque' ? 'bg-blue-50 text-blue-700'
                        : 'bg-amber-50 text-amber-700'
                    }`}>
                      {receipt.type}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-sm text-on-surface font-mono">{receipt.chequeNo || '—'}</td>
                  <td className="px-5 py-3.5 text-sm text-on-surface-variant">{receipt.dated || '—'}</td>
                  <td className="px-5 py-3.5 text-sm font-bold text-on-surface">₹{receipt.amount}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <section className="mt-2 rounded-2xl border border-outline-variant/20 bg-surface-container-low px-8 py-5">
        <div className="flex flex-col gap-1 text-xs text-on-surface-variant">
          {student?.createdBy && (
            <span>
              Created by <span className="font-bold text-on-surface">{student.createdBy}</span> on{' '}
              <span className="font-semibold text-on-surface">{student.createdDate}</span>
            </span>
          )}
          {student?.updatedBy && (
            <span>
              Updated by <span className="font-bold text-on-surface">{student.updatedBy}</span> on{' '}
              <span className="font-semibold text-on-surface">{student.updatedDate}</span>
            </span>
          )}
        </div>
      </section>
    </div>
    )}
    </>
  )
}
